/**
 * `api/menus.ts` — typed wrapper around the `headless-bridge` plugin's
 * `/wp-json/{namespace}/menus/{location}` endpoint. This is the only
 * module in the Frontend that talks to the menus route directly; every
 * hook and component consumes the nested `Menu` value returned by
 * {@link getMenu} instead of touching the raw REST payload (Req 4.1,
 * Req 4.2, Req 4.5).
 *
 * Responsibilities:
 * - Resolve the plugin namespace at module load from
 *   `VITE_HEADLESS_NAMESPACE`, falling back to `headless/v1` when the
 *   env var is not set. This keeps the route mirror-image of the
 *   backend registration in
 *   `cms/wp-content/plugins/headless-bridge/includes/class-rest-menus.php`
 *   (Req 2.3).
 * - Sanitize the caller-supplied `location` slug to a URL-safe subset
 *   (`[A-Za-z0-9_-]`) before splicing it into the request path so a
 *   hostile or malformed argument can never break the URL or trigger
 *   unexpected routing on the CMS side.
 * - Delegate response validation to {@link parseMenu} from
 *   `@/lib/menuTree`, which enforces the nested `Menu` / `MenuItem`
 *   contract shared with the backend `build_tree()` builder
 *   (Req 9.4). Any malformed payload surfaces as a `TypeError`-shaped
 *   `Error` from that parser; every non-2xx / transport failure
 *   surfaces as an `ApiError` from the shared response interceptor in
 *   `./client.ts` (Req 4.4).
 *
 * Requirements: 2.3, 4.1, 4.2, 9.4.
 */

import { apiClient } from './client';
import { parseMenu } from '@/lib/menuTree';
import type { Menu } from '@/types';

/**
 * WordPress REST namespace registered by the `headless-bridge` plugin.
 *
 * Resolved once at module load: the Vite env variable
 * `VITE_HEADLESS_NAMESPACE` wins when set (allowing per-environment
 * overrides via `frontend/.env`), otherwise we fall back to the
 * canonical `headless/v1` that the plugin registers by default. Kept
 * as a module constant so the string appears exactly once and every
 * helper below points at it.
 */
const NAMESPACE: string = import.meta.env.VITE_HEADLESS_NAMESPACE ?? 'headless/v1';

/**
 * Fetch a nav-menu tree by its registered theme location.
 *
 * The `location` argument identifies a `register_nav_menu()` slot on
 * the WordPress side (typically `'primary'` or `'footer'`). Before
 * being spliced into the request URL it is sanitized with a
 * conservative regex — any character outside `[A-Za-z0-9_-]` is
 * stripped — so a caller mistake or hostile input can neither break
 * the URL nor reach a route the plugin did not intend to expose.
 *
 * The plugin's `Headless_Bridge_Rest_Menus::build_tree()` helper
 * already returns the payload in the canonical `{ location, items }`
 * shape, so {@link parseMenu} is a validation + normalization pass
 * rather than a re-shaping step. Malformed payloads throw with a
 * descriptive error naming the offending field (Req 9.4).
 *
 * Rejections come back as the shared `ApiError` for HTTP / transport
 * failures (via the response interceptor in `./client.ts`), or as a
 * plain `Error` from {@link parseMenu} when the shape does not
 * validate.
 *
 * @param location - Theme-location slug identifying the nav menu on
 *   the WordPress side (e.g. `'primary'`, `'footer'`).
 * @returns The validated, strongly-typed {@link Menu} tree.
 */
export async function getMenu(location: string): Promise<Menu> {
  const sanitizedLocation = location.replace(/[^a-z0-9_-]/gi, '');

  const response = await apiClient.get<unknown>(
    `/wp-json/${NAMESPACE}/menus/${sanitizedLocation}`,
  );

  return parseMenu(response.data);
}
